import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import olMap from 'ol/Map'
import { IId } from "NSwag/ServerClient/Client.nswag";
import { TileArcGISRest, XYZ } from 'ol/source';
import ImageTile from 'ol/ImageTile';
import { fromLonLat } from 'ol/proj';
import TileGrid from 'ol/tilegrid/TileGrid';
import Tile from 'ol/Tile';
import { ClientConfig } from 'NSwag/ThinClient/Client.nswag'

interface IProps {
    config: ClientConfig,
    map: olMap
}

interface IState {
    tileLayer: TileLayer | null,
    error: string
}

/*
 * Define the actual component, its state if it has one
 * and its logic
 */
class OpenLayersTileLayer extends React.Component<IProps, IState> {

    id: IId;

    /**
    * sets the inital state for the tileLayer
    * @param { object } props - the set of properties associated with this component
    * @return { void }
    */
    constructor(props: IProps) {
        super(props);
        this.id = {
            name: "TileLayer",
            resourceType: "TileLayer",
            centerId: "OL",
            providerName: "OL",
        };

        this.state = {
            tileLayer: null,
            error: ''
        }
    }


    /**
    * checks to see if the component has been mounted yet
    * @return { void }
    */
    componentDidMount = () => {
        if (this.props.map) {
            this.addTileLayerToMap();
        }
    }

    /**
    * checks to see if the vtile layer has been created yet
    * @return { void }
    */
    componentDidUpdate = (prevProps: IProps) => {
        if (!prevProps.map && this.props.map) {
            this.addTileLayerToMap();
        }
    }

    /**
     * Method fired before component re-renders to determine if component should be re-rendered
     * @returns {boolean} - true if should be re-rendered; false if not.
     */
    shouldComponentUpdate = () => {
        let retVal = true;
        return retVal;
    }

    replacer = (match: string, letter: string, value: string): string => {
        var str = Number(value).toString(letter == 'L' ? 10 : 16);
        var len = letter == 'L' ? 2 : 8;
        while (str.length < len) {
            str = '0' + str;
        }
        return letter + str.toUpperCase();
    }

    loadTile = (imageTile: Tile, src: string) => {
        ((imageTile as ImageTile).getImage() as HTMLImageElement).src = src
            .replace(/(L)([0-9]+)/, this.replacer)
            .replace(/(R)([0-9]+)/, this.replacer)
            .replace(/(C)([0-9]+)/, this.replacer);
    }

    /**
    * add the tile layer to the map
    * @return { void }
    */
    addTileLayerToMap = () => {

        let { config } = this.props;
        let extent = config.mapConfig.extent;

        this.loadTileConfig()
            .then(tileInfo => {
                let lowerLeftPt = fromLonLat([extent.minLong, extent.minLat]);
                let upperRightPt = fromLonLat([extent.maxLong, extent.maxLat]);

                let tileLayer = new TileLayer({
                    source: new XYZ({
                        maxZoom: 8,
                        tileGrid: new TileGrid({
                            extent: [...lowerLeftPt, ...upperRightPt],
                            origin: [tileInfo.xOrigin, tileInfo.yOrigin],
                            resolutions: tileInfo.lods.map(lod => lod.resolution),
                            tileSize: [tileInfo.width, tileInfo.height]
                        }),
                        tileLoadFunction: this.loadTile,
                        url: `${config.tilesPath}/_alllayers/L0{z}/R{y}/C{x}.png`,
                    })
                });

                this.props.map.addLayer(tileLayer);
                this.setState({ tileLayer });
            }).catch(error => {
                console.log(error);
                this.setState({ error: "Unable to load tiles." })
            });
    }

    loadTileConfig = (): Promise<TileInfo> => {

        let { config } = this.props;

        return new Promise<TileInfo>((resolve, reject) => {
            fetch(`${config.tilesPath!}/conf.xml`)
                .then(resp => resp.text())
                .then(text => {
                    let parser = new DOMParser();
                    let xmlDoc = parser.parseFromString(text, 'text/xml');
                    let xOrigin = parseFloat(xmlDoc.getElementsByTagName("X")[0].childNodes[0].nodeValue!);
                    let yOrigin = parseFloat(xmlDoc.getElementsByTagName("Y")[0].childNodes[0].nodeValue!);
                    let width = parseInt(xmlDoc.getElementsByTagName("TileCols")[0].childNodes[0].nodeValue!);
                    let height = parseInt(xmlDoc.getElementsByTagName("TileRows")[0].childNodes[0].nodeValue!);
                    let lods = this.parseLayers(xmlDoc);

                    resolve({
                        xOrigin,
                        yOrigin,
                        width,
                        height,
                        lods
                    });

                }).catch(error => {
                    reject(error);
                })
        });
    }

    parseLayers = (xmlDoc: Document): LODInfo[] => {
        let layers: LODInfo[] = [];
        let elems = xmlDoc.getElementsByTagName("LODInfo");

        for (let i = 0; i < elems.length; i++) {
            let layerId = parseInt(elems[i].getElementsByTagName("LevelID")[0].childNodes[0].nodeValue!);
            let scale = parseFloat(elems[i].getElementsByTagName("Scale")[0].childNodes[0].nodeValue!);
            let resolution = parseFloat(elems[i].getElementsByTagName("Resolution")[0].childNodes[0].nodeValue!);
            layers.push({ layerId, scale, resolution });
        }

        return layers;
    }

    /**
    * Define how the component will render (html elements or other components)
    * @return { null } returns null to indicate that it does not render
    */
    render() {
        return null;
    }
}

/*
 * Make our component the default export object and attach the styles defined
 */
export default OpenLayersTileLayer

interface TileInfo {
    xOrigin: number,
    yOrigin: number,
    width: number,
    height: number,
    lods: LODInfo[]
}

interface LODInfo {
    layerId: number,
    scale: number,
    resolution: number
}